import { useState } from "react";
import { BadgeCheck, Compass, MessagesSquare, X } from "lucide-react";
import {
  ASSETS,
  COMPANY,
  SHOW_SHOP_GALLERY,
  SHOP_GALLERY_LABEL,
} from "@/data/products";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const VALUES = [
  {
    icon: BadgeCheck,
    title: "Clear product information",
    text: "Each listing carries its own price in MYR, category and supplied condition — nothing is generalised across the catalogue.",
  },
  {
    icon: Compass,
    title: "Item-specific condition details",
    text: "New, pre-owned and please-enquire items are labelled individually so you know exactly what is being offered.",
  },
  {
    icon: MessagesSquare,
    title: "Helpful purchasing communication",
    text: "Availability, condition and order details are confirmed directly with you over WhatsApp before any payment.",
  },
];

export default function About() {
  const [enlarged, setEnlarged] = useState(null);

  return (
    <section id="about" className="scroll-mt-20 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-bronze-dark">
          About Us
        </p>
        <h2 className="mt-3 font-display text-3xl font-semibold text-charcoal sm:text-4xl">
          A fishing equipment retailer in Sungai Petani, Kedah
        </h2>
        <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-4 text-sm leading-relaxed text-charcoal/75 sm:text-base">
            <p>
              {COMPANY.name} is a Malaysian retail business based in Sungai
              Petani, Kedah. Our business scope covers the retail sale of
              fishing equipment, the retail sale of products over the internet,
              and the retail sale of second-hand goods.
            </p>
            <p>
              Our catalogue currently focuses on fishing products — reels,
              electric reels and rods — with every listing showing its supplied
              condition, whether new, pre-owned or available on enquiry. We
              focus on clear product information, item-specific condition
              details and helpful purchasing communication, so you can decide
              with confidence before ordering.
            </p>
            <blockquote className="rounded-xl border-l-4 border-bronze bg-cream p-5">
              <p className="font-display text-lg italic text-forest">
                “To help customers explore suitable products through clear
                information and helpful communication.”
              </p>
              <cite className="mt-2 block text-xs font-semibold uppercase tracking-[0.15em] not-italic text-charcoal/60">
                Our Mission
              </cite>
            </blockquote>
            <dl className="rounded-xl border border-charcoal/10 bg-cream/60 p-5 text-sm">
              <div className="flex flex-col gap-1 sm:flex-row sm:gap-3">
                <dt className="font-semibold text-charcoal">Registered name:</dt>
                <dd className="text-charcoal/75">{COMPANY.name}</dd>
              </div>
              <div className="mt-2 flex flex-col gap-1 sm:flex-row sm:gap-3">
                <dt className="font-semibold text-charcoal">SSM registration:</dt>
                <dd className="text-charcoal/75">{COMPANY.ssm}</dd>
              </div>
            </dl>
          </div>
          <div className="space-y-5">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  className="flex gap-4 rounded-2xl border border-charcoal/10 bg-cream/50 p-5"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest text-cream">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal">{v.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-charcoal/65">{v.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {SHOW_SHOP_GALLERY && ASSETS.shop.length > 0 && (
          <div className="mt-14" data-testid="shop-gallery">
            <h3 className="font-display text-2xl font-semibold text-charcoal">
              {SHOP_GALLERY_LABEL}
            </h3>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <button
                type="button"
                onClick={() => setEnlarged(ASSETS.shop[0])}
                className="overflow-hidden rounded-2xl border border-charcoal/10 md:col-span-2 md:row-span-2"
                aria-label="Enlarge shop photograph 1"
                data-testid="shop-photo-1"
              >
                <img
                  src={ASSETS.shop[0]}
                  alt={`${SHOP_GALLERY_LABEL} — photograph 1`}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </button>
              {ASSETS.shop.slice(1).map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setEnlarged(src)}
                  className="overflow-hidden rounded-2xl border border-charcoal/10"
                  aria-label={`Enlarge shop photograph ${i + 2}`}
                  data-testid={`shop-photo-${i + 2}`}
                >
                  <img
                    src={src}
                    alt={`${SHOP_GALLERY_LABEL} — photograph ${i + 2}`}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={Boolean(enlarged)} onOpenChange={() => setEnlarged(null)}>
        <DialogContent className="max-w-4xl border-none bg-transparent p-0 shadow-none">
          <DialogTitle className="sr-only">Enlarged shop photograph</DialogTitle>
          {enlarged && (
            <div className="relative">
              <img
                src={enlarged}
                alt="Enlarged shop photograph"
                className="max-h-[80vh] w-full rounded-xl object-contain"
              />
              <button
                type="button"
                onClick={() => setEnlarged(null)}
                className="absolute right-3 top-3 rounded-full bg-charcoal/80 p-2 text-cream"
                aria-label="Close enlarged photograph"
                data-testid="shop-lightbox-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
